"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Info, RefreshCcw, X } from "lucide-react";
import { useIntlayer } from "next-intlayer";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";

export default function ServiceWorkerRegister() {
  const [showBanner, setShowBanner] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const { updateAvailable, updateDescription, updateButton, dismissButton } =
    useIntlayer("service-worker");

  useEffect(() => {
    // Check if browser supports service workers and we are in production/client-side
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }

    let refreshing = false;

    // Reload the page once the new service worker takes over
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });

    const offerUpdate = (reg: ServiceWorkerRegistration) => {
      setRegistration(reg);
      setShowBanner(true);
    };

    // Register the service worker
    navigator.serviceWorker
      .register("/sw.js", { updateViaCache: "none" })
      .then((reg) => {
        // 1. If there's already a worker waiting, offer update immediately
        if (reg.waiting && navigator.serviceWorker.controller) {
          offerUpdate(reg);
          return;
        }

        // 2. If a new worker is installing, listen for state changes
        reg.addEventListener("updatefound", () => {
          const newWorker = reg.installing;
          if (!newWorker) return;

          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
              offerUpdate(reg);
            }
          });
        });
      })
      .catch((error) => {
        console.error("Service Worker registration failed:", error);
      });
  }, []);

  const handleUpdate = () => {
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
    }
    setShowBanner(false);
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.95 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="fixed bottom-6 right-6 z-50 max-w-md w-[calc(100vw-3rem)] p-5 rounded-2xl border border-border bg-card/85 backdrop-blur-md shadow-2xl space-y-4"
        >
          {/* Card header */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="bg-primary/10 p-2 rounded-xl flex items-center justify-center">
                <Info className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-black text-lg tracking-tight">{updateAvailable.value}</h3>
            </div>
            <button
              onClick={() => setShowBanner(false)}
              className="text-muted-foreground hover:text-foreground p-1 hover:bg-muted rounded-lg transition-colors cursor-pointer"
              aria-label="Dismiss banner"
              type="button"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Description */}
          <p className="text-muted-foreground text-sm leading-relaxed">{updateDescription.value}</p>

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBanner(false)}
              className="rounded-full cursor-pointer"
            >
              {dismissButton.value}
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleUpdate}
              className="rounded-full gap-2 shadow-md shadow-primary/15 cursor-pointer"
            >
              <RefreshCcw className="w-3.5 h-3.5" />
              {updateButton.value}
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
